import { useCallback, useEffect, useRef, useState } from 'react';
import './BoardViewer.scss';
import DataGrid, { Toolbar, Item, SearchPanel, Column, Paging, LoadPanel as LoadPanelDg, Scrolling, Pager, GroupPanel, ColumnChooser, FilterRow, Export } from 'devextreme-react/data-grid';
import { Button, Tooltip } from 'devextreme-react';
import { useApiReports } from '../../../api/api.reports';
import { FormPopupSimpleItem } from '../..';
import { FormField } from '../../../types/types.system';
import { filter } from '../../../images/icons';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';
import { exportDataGrid } from 'devextreme/excel_exporter';

interface BoardViewerProps {
  board: any; // El tablero seleccionado con Rp_Id, Rp_Nombre, Rp_Query, Rp_Filtros
  onClose?: () => void; // Callback opcional para cerrar el viewer
  showCloseButton?: boolean; // Mostrar botón de cerrar
  height?: string; // Altura personalizada del grid
}

const dataFiltered: any = {};

export const BoardViewer = ({ board, onClose, showCloseButton = false, height = 'calc(100vh - 200px)' }: BoardViewerProps) => {
  const { runBoardReport } = useApiReports();
  const [filtrosVisible, setFiltrosVisible] = useState<boolean>(false);
  const [filtros, setFiltros] = useState<FormField[]>([]);
  const [filtersApplied, setFiltersApplied] = useState<any>([]);
  const [sending, setSending] = useState<boolean>(false);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [visibleCount, setVisibleCount] = useState<number>(0);
  const gridRef = useRef<DataGrid>(null);

  // Resetear estado cuando cambia el tablero
  useEffect(() => {
    if (!board) return;
    
    setFilteredData([]);
    setFiltersApplied([]);
    
    try {
      const parsedFilters: FormField[] = JSON.parse(board.Rp_Filtros || '[]');
      setFiltros(parsedFilters);
      
      // Auto-abrir filtros al seleccionar un tablero nuevo
      if (parsedFilters.length > 0) {
        setFiltrosVisible(true);
      }
    } catch (e) {
      console.error('Error parsing filters:', e);
      setFiltros([]);
    }
  }, [board?.Rp_Id]);

  const handleClicFilter = useCallback(() => {
    const filters = filtros.map((field) => {
      let values = dataFiltered[field.equivalence];

      if (!Array.isArray(values)) {
        values = [values];
      }

      if (field.type === 'dxDateRangeBox' && values.length === 2) {
        const startDate = new Date(values[0]);
        const endDate = new Date(values[1]);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        values = [startDate, endDate];
      }

      return {
        Field: field.equivalence,
        Operator: field.typeCondition || '=',
        Values: values.filter((value) => value !== null),
      };
    });
    setFiltersApplied(filters);
    fetchData(filters);
  }, [filtros, board?.Rp_Query]);

  const fetchData = useCallback((filters) => {
    if (!filters || !board?.Rp_Query) return;
    
    setSending(true);
    runBoardReport(filters, board.Rp_Query)
      .then((res) => {
        setFilteredData(res);
      })
      .finally(() => {
        setFiltrosVisible(false);
        setSending(false);
      });
  }, [board?.Rp_Query, runBoardReport]);

  const handleClicRefresh = useCallback(() => {
    fetchData(filtersApplied);
  }, [filtersApplied, fetchData]);

  const handleVisibleFilter = useCallback(() => {
    setFiltrosVisible(false);
  }, []);

  const onExporting = useCallback(
    (e) => {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Main sheet');
      exportDataGrid({
        component: e.component,
        worksheet,
        autoFilterEnabled: true,
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `INFORME - ${board?.Rp_Nombre || 'Tablero'}.xlsx`);
        });
      });
    },
    [board]
  );

  if (!board) {
    return (
      <div className='board-viewer-empty'>
        <i className='dx-icon dx-icon-detailslayout'></i>
        <h3>No hay tablero seleccionado</h3>
        <p>Selecciona un tablero de control para comenzar a visualizar datos</p>
      </div>
    );
  }

  return (
    <div className='board-viewer-container'>
      <DataGrid
        height={height}
        allowColumnResizing={true}
        columnResizingMode='nextColumn'
        className='board-viewer-grid'
        ref={gridRef}
        onExporting={onExporting}
        dataSource={filteredData}
        rowAlternationEnabled={true}
        onContentReady={(e) => {
          setVisibleCount(e.component.totalCount());
        }}>
        <LoadPanelDg enabled={true} />
        <Export enabled={true} />
        <ColumnChooser enabled={true} mode='select' />
        <FilterRow visible={true} applyFilter='auto' />
        <GroupPanel visible={true} />
        <Scrolling mode='standard' columnRenderingMode='virtual' />
        <SearchPanel visible placeholder='Buscar' />
        <Paging enabled={true} />
        <Pager visible={true} allowedPageSizes={[20, 50, 100, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
        <Toolbar>
          <Item location='before' showText='always'>
            <div className='grid-header'>
              {board.Rp_Nombre} {gridRef.current?.instance ? `(${gridRef.current.instance.totalCount()} filas)` : ''}
            </div>
          </Item>

          {showCloseButton && (
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button icon='close' text='Cerrar' stylingMode='text' onClick={onClose} />
            </Item>
          )}

          <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Tooltip target='.filter-button-board' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
              Filtrar
            </Tooltip>
            <Button className='filter-button-board' icon={filter} text='Filtrar' stylingMode='text' onClick={() => setFiltrosVisible(true)} />
          </Item>

          <Item location='before' name='groupPanel' />

          <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='refreshRpBoard' onClick={handleClicRefresh} icon='refresh' text='Refrescar' stylingMode='text' />
            <Tooltip target={'#refreshRpBoard'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Refrescar datos
            </Tooltip>
          </Item>

          <Item location='after' name='exportButton' />
          <Item location='after' name='columnChooserButton' />
          <Item location='after' name='searchPanel' />
        </Toolbar>
      </DataGrid>

      <FormPopupSimpleItem dataSource={dataFiltered} isSaveDisabled={false} loading={sending} onSave={handleClicFilter} title='Seleccione los filtros' visible={filtrosVisible} setVisible={handleVisibleFilter} fields={filtros} />
    </div>
  );
};
